import React from 'react';
import { Users, TrendingUp, Clock, Target, Award, AlertCircle } from 'lucide-react';

/**
 * PipelineOverview - Collapsible overview showing aggregated stats across all jobs
 * Features: KPI cards, trends, conversion funnel, bottleneck alerts
 */
const PipelineOverview = ({ 
  isVisible = true, 
  onToggle, 
  overviewData = {},
  loading = false 
}) => {
  const {
    totalJobs = 0,
    totalApplications = 0,
    totalActiveJobs = 0,
    averageTimeToHire = 0,
    overallConversionRate = 0,
    topBottlenecks = [],
    stageDistribution = {},
    recentTrends = {}
  } = overviewData;

  const stages = [
    { id: 'new', name: 'Applied', color: 'bg-blue-500' },
    { id: 'screening', name: 'Screening', color: 'bg-amber-500' },
    { id: 'interview', name: 'Interview', color: 'bg-purple-500' },
    { id: 'final-review', name: 'Final Review', color: 'bg-indigo-500' },
    { id: 'offer', name: 'Offer', color: 'bg-emerald-500' },
    { id: 'hired', name: 'Hired', color: 'bg-green-600' }
  ];

  if (!isVisible) {
    return (
      <div className="mb-6">
        <button
          onClick={onToggle}
          className="flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm border border-gray-200 rounded-xl hover:bg-white transition-colors"
        >
          <TrendingUp className="w-4 h-4 text-gray-600" />
          <span className="text-sm text-gray-700">Show Pipeline Overview</span>
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="mb-8 bg-gradient-to-br from-slate-50/80 to-gray-100/60 backdrop-blur-sm border border-white/60 rounded-3xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="w-48 h-6 bg-gray-200 rounded animate-pulse" />
          <div className="w-20 h-8 bg-gray-200 rounded animate-pulse" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          {[...Array(4)]?.map((_, i) => (
            <div key={i} className="bg-white/60 rounded-2xl p-4 animate-pulse">
              <div className="w-8 h-8 bg-gray-200 rounded-xl mb-3" />
              <div className="w-16 h-8 bg-gray-200 rounded mb-2" />
              <div className="w-24 h-4 bg-gray-200 rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="mb-8 bg-gradient-to-br from-slate-50/80 to-gray-100/60 backdrop-blur-sm border border-white/60 rounded-3xl p-6 shadow-lg shadow-gray-200/50">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-1">
            Pipeline Overview
          </h2>
          <p className="text-sm text-gray-600">
            Aggregated metrics across all active positions
          </p>
        </div>
        
        <button
          onClick={onToggle}
          className="px-4 py-2 bg-white/60 hover:bg-white/80 border border-gray-200 rounded-xl transition-colors text-sm text-gray-700"
        >
          Hide Overview
        </button>
      </div>
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white/60 backdrop-blur-sm border border-white/80 rounded-2xl p-4 hover:shadow-md transition-all">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-8 h-8 bg-violet-100 rounded-xl flex items-center justify-center">
              <Users className="w-4 h-4 text-violet-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{totalApplications}</div>
              <div className="text-xs text-gray-600">Total Applications</div>
            </div>
          </div>
          {recentTrends?.applications && (
            <div className="flex items-center gap-1 text-xs">
              <TrendingUp className="w-3 h-3 text-emerald-500" />
              <span className="text-emerald-600 font-medium">
                +{recentTrends?.applications}% this week
              </span>
            </div>
          )}
        </div>

        <div className="bg-white/60 backdrop-blur-sm border border-white/80 rounded-2xl p-4 hover:shadow-md transition-all">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-8 h-8 bg-amber-100 rounded-xl flex items-center justify-center">
              <Target className="w-4 h-4 text-amber-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{totalActiveJobs}</div>
              <div className="text-xs text-gray-600">Active Positions</div>
            </div>
          </div>
          <div className="text-xs text-gray-500">
            {totalJobs} total positions
          </div>
        </div>

        <div className="bg-white/60 backdrop-blur-sm border border-white/80 rounded-2xl p-4 hover:shadow-md transition-all">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-8 h-8 bg-blue-100 rounded-xl flex items-center justify-center">
              <Clock className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{Math.round(averageTimeToHire)}</div>
              <div className="text-xs text-gray-600">Avg Days to Hire</div>
            </div>
          </div>
          <div className="text-xs text-gray-500">
            Industry avg: 23 days
          </div>
        </div>

        <div className="bg-white/60 backdrop-blur-sm border border-white/80 rounded-2xl p-4 hover:shadow-md transition-all">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-8 h-8 bg-emerald-100 rounded-xl flex items-center justify-center">
              <Award className="w-4 h-4 text-emerald-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{overallConversionRate?.toFixed(1)}%</div>
              <div className="text-xs text-gray-600">Conversion Rate</div>
            </div>
          </div>
          <div className="text-xs text-gray-500">
            Application to hire
          </div>
        </div>
      </div>
      {/* Stage Distribution & Bottlenecks */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Stage Funnel */}
        <div className="lg:col-span-2 bg-white/40 backdrop-blur-sm border border-white/60 rounded-2xl p-5">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-violet-600" />
            Pipeline Distribution
          </h3>
          
          <div className="space-y-3">
            {stages?.map((stage) => {
              const count = stageDistribution?.[stage?.id] || 0;
              const percentage = totalApplications > 0 ? (count / totalApplications) * 100 : 0;
              
              return (
                <div key={stage?.id} className="flex items-center gap-3">
                  <div className="w-20 text-sm text-gray-700 font-medium">
                    {stage?.name}
                  </div>
                  <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
                    <div
                      className={`${stage?.color} h-full transition-all duration-500 ease-out`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <div className="w-12 text-right text-sm text-gray-600 font-medium">
                    {count}
                  </div>
                  <div className="w-12 text-right text-xs text-gray-500">
                    {percentage?.toFixed(0)}%
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Bottlenecks Alert */}
        <div className="bg-white/40 backdrop-blur-sm border border-white/60 rounded-2xl p-5">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-amber-600" />
            Bottleneck Alerts
          </h3>
          
          {topBottlenecks?.length > 0 ? (
            <div className="space-y-3">
              {topBottlenecks?.slice(0, 3)?.map((bottleneck, index) => (
                <div
                  key={index}
                  className="flex items-start gap-3 p-3 bg-amber-50/60 border border-amber-200/60 rounded-xl"
                >
                  <div className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0" />
                  <div>
                    <div className="text-sm font-medium text-amber-800">
                      {bottleneck?.stage}
                    </div>
                    <div className="text-xs text-amber-700">
                      {bottleneck?.issue}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6">
              <Award className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
              <p className="text-sm text-gray-600">
                No bottlenecks detected
              </p>
              <p className="text-xs text-gray-500">
                Pipeline is flowing smoothly
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PipelineOverview;